// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { components as CONFIG_COMPONENTS, application as CONFIG_APP } from 'Config';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';
import { EnquireOfferFormModel } from 'shared_models/forms/EnquireOffer';
import { TermsSection } from 'shared_components/forms/common/TermsSection';
import { selectUserId } from 'shared_services/redux/selectors/user';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { DescriptionSection } from 'shared_components/forms/enquire/sections/Description';
import { WithEnquireSuccess } from 'shared_hocs/forms/WithEnquireSuccess';
import { HOCEnquireForm } from 'shared_hocs/forms/Enquire';
import { gtmArtworkOfferOpen } from 'shared_data/providers/google/tagmanager/Actions';
import { artworkOfferRequestSend } from 'shared_services/redux/actions/application/enquire';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { WithAclComponent } from 'shared_components/common/auth/WithAclComponent';
import CREATE_OFFER from 'shared_data/queries/offer/create.graphql';

import { enquireFormCls } from 'shared_components/forms/enquire/Form.less';

const EnquireFormSection = HOCEnquireForm({
  actionOpen: gtmArtworkOfferOpen,
  actionSubmit: artworkOfferRequestSend,
});

type Props = {
  storeCode: string,
  artwork: Object,
  price: string | number,
  selectedFrame: ?Object,
  trigger: string,
  onClose: Function,
};

/**
 * EnquireOfferForm
 */
export const EnquireOfferForm = ({
  artwork,
  price,
  selectedFrame,
  storeCode,
  trigger,
  onClose,
}: Props): Node => {
  const { formatMessage } = useIntl();
  const actionLabel = formatMessage({ id: 'forms.enquireOffer.submitBtn' });
  const userId = useSelector(selectUserId);
  const locale = useLocale();
  const [createOffer, { loading }] = useMutation(CREATE_OFFER);
  const { sku, sellerOfferMaximumDiscount } = (artwork && artwork.productPrimarySku) || {};
  const sellerMaxDiscount = sellerOfferMaximumDiscount && parseInt(sellerOfferMaximumDiscount, 10);
  const maxAllowedDiscountPercent =
    (!Number.isNaN(sellerMaxDiscount) && sellerMaxDiscount) ||
    CONFIG_COMPONENTS.offer.maxAllowedDiscountPercent;

  return (
    <WithAclComponent
      resourcePermissions={CONFIG_APP.acl.resourcePermissions.offer.create}
      skipRedirect
    >
      <WithEnquireSuccess
        onClose={onClose}
        message={formatMessage({ id: 'forms.emailEnquire.successOffer' })}
      >
        {({ onSubmit }) => (
          <section className={enquireFormCls}>
            <DescriptionSection phone={LocalizedConfig.get('contact.advisor.phone')}>
              {formatMessage(
                { id: 'forms.enquireOffer.description' },
                {
                  artworkName: artwork.title,
                  artistName: artwork.artist && artwork.artist.name,
                  strong: (chunk) => <strong>{chunk}</strong>,
                },
              )}
            </DescriptionSection>
            <IsomorphicRipple isActive={loading}>
              <EnquireFormSection
                onSubmit={async (values, props, formRef) => {
                  createOffer({
                    variables: {
                      inputOffer: {
                        store: storeCode,
                        userId,
                        productSku: sku,
                        offerAmount: values.offer,
                        customerEmail: values.email,
                        ...(values.firstName ? { customerFirstName: values.firstName } : null),
                        ...(values.lastName ? { customerLastName: values.lastName } : null),
                        ...(values.phone ? { customerPhone: values.phone } : null),
                        ...(values.message ? { comment: values.message } : null),
                      },
                    },
                  })
                    .then(() => onSubmit(values, props, formRef))
                    .catch(() => null);
                }}
                submitActionLabel={actionLabel}
                trackedData={{
                  artwork,
                  trigger,
                  ...(selectedFrame && selectedFrame.id ? { selectedFrame } : {}),
                }}
                additionalInitialValues={{
                  productPrice: price
                    ? formatCurrency(price, storeCode, undefined, {
                        locale: locale && locale.name,
                        hideCurrencyCode: true,
                      })
                    : null,
                }}
                formSettings={EnquireOfferFormModel({
                  formatMessage,
                  productPrice: price,
                  maxAllowedDiscountPercent,
                })}
              />
            </IsomorphicRipple>
            <TermsSection translationValues={{ actionLabel }} />
          </section>
        )}
      </WithEnquireSuccess>
    </WithAclComponent>
  );
};
